/* The adhan, played in the app at each prayer time you've turned on.

   Why it used to be silent on iPhone: it was started with `new Audio().play()`
   from a service-worker message — no tap involved — and iOS refuses audio
   that a user gesture didn't start. Now:
   - one audio element is "unlocked" by your first tap anywhere in the app
     (a silent play), after which iOS lets it play on its own;
   - the page schedules the adhan itself while the app is open, instead of
     relying on the service worker's timers (which iOS suspends);
   - the service worker's PLAY_ADHAN still works as a second trigger, and a
     per-prayer guard stops it playing twice.

   Phone locked (opt-in "Adhan when locked"): iOS only keeps a Home Screen app
   running in the background while it plays audio. So when the app is hidden,
   the same (already unlocked) element loops silence; its timeupdate events
   keep arriving while locked, and at prayer time it switches to the adhan —
   no new permission needed, because that element is already playing. The cost:
   iOS has one audio session, so it pauses other apps' music while active, and
   iOS may still end it under memory pressure.

   With the app swiped away, a web app can't play its own sound — iOS (and
   Chrome) use the standard notification sound for web push. */
import { useEffect } from 'react';
import { create } from 'zustand';
import { SALAH, PRAYER_LABEL, addDays, dayKeyIn, timesFor, type PrayerKey } from '@shared/prayer';
import { useSettings } from '@/lib/store';
import { silenceUrl, usePlayer } from '@/features/player/audioEngine';
import { useRuqyah } from '@/features/adhkar/ruqyahPlayer';
import { chimeUrl, playChimeOver, unlockChime } from './chime';

export const ADHAN_URL: Record<string, string> = {
  makkah: 'https://cdn.aladhan.com/audio/adhans/a1.mp3',
  madinah: 'https://cdn.aladhan.com/audio/adhans/a2.mp3',
};

export const useAdhan = create<{ playing: PrayerKey | 'preview' | null; ready: { key: PrayerKey | 'preview'; at: number } | null }>(() => ({ playing: null, ready: null }));

/* What the locked-screen keep-awake is waiting for: the next adhan, or a pending test. */
let keepAwake = false;
let testAt: { at: number; onDone?: (ok: boolean) => void } | null = null;

let el: HTMLAudioElement | null = null;
let unlocked = false;
let unlocking = false;
const audio = () => {
  if (!el) {
    el = new Audio();
    el.preload = 'none';
    el.addEventListener('ended', () => { useAdhan.setState({ playing: null }); releaseControls(); });
    el.addEventListener('pause', () => {
      if (keepAwake) { keepAwake = false; useAdhan.setState({ ready: null }); } // stopped by us, or interrupted by another app's audio
      if (!el!.ended && !unlocking) useAdhan.setState({ playing: null });
    });
    el.addEventListener('timeupdate', onKeepAwakeTick);
  }
  return el;
};

/* Lock-screen pause, Control Centre, AirPods/headphone taps → stop the adhan.
   (The recitation and ruqyah players register their own handlers when they
   play, so without this a lock-screen "pause" during the adhan paused the
   wrong player.) Web apps can't see the hardware volume buttons. */
function claimControls() {
  if (!('mediaSession' in navigator)) return;
  const ms = navigator.mediaSession;
  const set = (a: MediaSessionAction, h: MediaSessionActionHandler | null) => { try { ms.setActionHandler(a, h); } catch {} };
  set('pause', () => stopAdhan());
  set('stop', () => stopAdhan());
  set('play', null);
  set('nexttrack', null);
  set('previoustrack', null);
  set('seekto', null);
}
function releaseControls() {
  if ('mediaSession' in navigator) navigator.mediaSession.metadata = null;
}

/** Call from a user gesture: lets iOS play the adhan later without one. */
export function unlockAdhan() {
  unlockChime();
  if (unlocked) return;
  const a = audio();
  unlocking = true;
  const silence = silenceUrl(200);
  a.src = silence;
  /* Only pause our own silence: if the adhan started meanwhile (prayer time right after the tap),
     pausing would stop it. The adhan also waits for this to settle (unlockDone). */
  unlockDone = a.play().then(() => { if (a.src === silence) a.pause(); unlocked = true; }).catch(() => {}).finally(() => { unlocking = false; });
}
let unlockDone: Promise<void> | null = null;

const PLAYED_KEY = 'ftu_adhan_played';
const alreadyPlayed = (tag: string) => { try { return sessionStorage.getItem(PLAYED_KEY) === tag; } catch { return false; } };
const markPlayed = (tag: string) => { try { sessionStorage.setItem(PLAYED_KEY, tag); } catch {} };

/** Play the adhan (or a preview). When the adhan itself can't play, the prayer-time chime
    does (see chime.ts). Returns false if nothing could start (sound off, or blocked). */
export async function playAdhan(which: PrayerKey | 'preview', tag?: string): Promise<boolean> {
  const sound = useSettings.getState().settings.adhanSound;
  if (sound === 'none' && which !== 'preview') return false;
  if (tag && alreadyPlayed(tag)) return false;
  /* Two triggers fire at prayer time (the page's own timer and the service worker). A second
     start would interrupt the first one's loading (AbortError), so it is ignored while one runs. */
  if (tag && starting === tag) return false;
  starting = tag ?? null;
  try { return await startAdhan(which, sound, tag); } finally { if (starting === (tag ?? null)) starting = null; }
}
let starting: string | null = null;

async function startAdhan(which: PrayerKey | 'preview', sound: ReturnType<typeof useSettings.getState>['settings']['adhanSound'], tag?: string): Promise<boolean> {
  if (unlocking && unlockDone) await unlockDone;   // a tap's unlock still settling: let it finish first
  // Never talk over the Qurʾān or ruqyah: a soft chime is mixed in instead, and the recitation carries on.
  if (which !== 'preview' && (usePlayer.getState().status === 'playing' || useRuqyah.getState().status === 'playing')) {
    const ok = await playChimeOver();
    if (ok && tag) markPlayed(tag);
    return ok;
  }
  const chime = sound === 'chime';   // otherwise always the adhan; the chime only if it can't load
  const start = async (src: string, isChime: boolean) => {
    const a = audio();
    a.src = src;
    await a.play();
    if (tag) markPlayed(tag);
    useAdhan.setState({ playing: which });
    claimControls();
    if ('mediaSession' in navigator && which !== 'preview') {
      navigator.mediaSession.metadata = new MediaMetadata({ title: `${isChime ? 'Prayer time' : 'Adhan'} · ${PRAYER_LABEL[which].en}`, artist: 'For The Ummah', artwork: [{ src: `${location.origin}/icons/artwork-512.png`, sizes: '512x512', type: 'image/png' }] });
    }
  };
  try {
    await start(chime ? chimeUrl() : ADHAN_URL[sound === 'none' ? 'makkah' : sound], chime);
    return true;
  } catch (e) {
    /* Only a recording that can't load (no signal, server down: NotSupportedError, or a network
       error on the element) falls back to the chime. Not allowed (iOS: no tap since the app opened)
       means nothing can play; an interrupted start (AbortError) means the adhan is already starting. */
    const err = audio().error?.code;
    const unloadable = (e as Error)?.name === 'NotSupportedError' || err === MediaError.MEDIA_ERR_NETWORK || err === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED;
    if (chime || !unloadable) return false;
    try { await start(chimeUrl(), true); return true; } catch { return false; }
  }
}

/** Settings → "Test the adhan in 30 seconds": the same path as prayer time — scheduled
    in the page, played without a tap — so it proves the phone will really play it. */
let testTimer = 0;
export function scheduleTestAdhan(seconds = 30, onDone?: (played: boolean) => void) {
  clearTimeout(testTimer);
  testAt = { at: Date.now() + seconds * 1000, onDone };
  // Foreground: a plain timer. Locked: the keep-awake tick fires it (timers are suspended there).
  testTimer = window.setTimeout(() => void fireTest(), seconds * 1000);
}
async function fireTest() {
  const t = testAt;
  if (!t) return;
  testAt = null;
  clearTimeout(testTimer);
  t.onDone?.(await playAdhan('preview', `test-${t.at}`));
}

/** The next thing the keep-awake should wake for. */
function nextTarget(): { key: PrayerKey | 'preview'; at: number; tag: string } | null {
  const n = nextAdhan();
  const prayer = n ? { key: n.key, at: n.at.getTime(), tag: n.tag } : null;
  const test = testAt ? { key: 'preview' as const, at: testAt.at, tag: `test-${testAt.at}` } : null;
  return [prayer, test].filter((x): x is NonNullable<typeof x> => !!x).sort((a, b) => a.at - b.at)[0] ?? null;
}

/** App hidden (locked / switched away): loop silence so iOS keeps the app — and the adhan — alive. */
export function startKeepAwake() {
  const s = useSettings.getState().settings;
  if (!s.adhanWhenLocked || keepAwake || !unlocked || useAdhan.getState().playing) return;
  // Recitation or ruqyah already keeps the app awake (and the adhan politely doesn't talk over them).
  if (usePlayer.getState().status === 'playing' || useRuqyah.getState().status === 'playing') return;
  if (s.adhanSound === 'none' && !testAt) return;
  const t = nextTarget();
  if (!t) return;
  const a = audio();
  a.loop = true;
  a.src = silenceUrl(10_000);
  keepAwake = true;
  a.play().then(() => {
    useAdhan.setState({ ready: { key: t.key, at: t.at } });
    claimControls(); // lock-screen pause on "Adhan ready" turns the wait off
    if ('mediaSession' in navigator) {
      const what = t.key === 'preview' ? 'Adhan test' : `Adhan ready · ${PRAYER_LABEL[t.key].en}`;
      navigator.mediaSession.metadata = new MediaMetadata({ title: what, artist: `For The Ummah · ${new Date(t.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`, artwork: [{ src: `${location.origin}/icons/artwork-512.png`, sizes: '512x512', type: 'image/png' }] });
    }
  }).catch(() => { keepAwake = false; });
}

export function stopKeepAwake() {
  if (!keepAwake || !el) return;
  el.loop = false;
  el.pause(); // the pause handler clears keepAwake/ready
}

/* While the silence loops (also when locked), timeupdate keeps firing: check the clock. */
function onKeepAwakeTick() {
  if (!keepAwake || !el) return;
  const t = nextTarget();
  if (!t || Date.now() < t.at - 500) return;
  keepAwake = false;
  el.loop = false;
  useAdhan.setState({ ready: null });
  if (t.key === 'preview') void fireTest();
  else if (!alreadyPlayed(t.tag)) void playAdhan(t.key, t.tag);
}

export function stopAdhan() {
  if (el) { el.loop = false; el.pause(); el.currentTime = 0; }
  useAdhan.setState({ playing: null });
  releaseControls();
}

/** The next prayer whose adhan is switched on, from `now` (today, then tomorrow's Fajr). */
export function nextAdhan(now = new Date()): { key: PrayerKey; at: Date; tag: string } | null {
  const { prayer, notify } = useSettings.getState().settings;
  const today = dayKeyIn(prayer.tz, now);
  for (const day of [today, addDays(today, 1)]) {
    const t = timesFor(prayer, day);
    for (const k of SALAH) {
      if (notify.adhan?.[k] && t[k].getTime() > now.getTime() - 30_000) return { key: k, at: t[k], tag: `${day}:adhan-${k}` };
    }
  }
  return null;
}

/** Mount once. */
export function usePlayAdhan() {
  const prayer = useSettings((s) => s.settings.prayer);
  const adhanPrefs = useSettings((s) => s.settings.notify.adhan);

  // First tap anywhere unlocks the audio element for later.
  useEffect(() => {
    const on = () => unlockAdhan();
    const opts = { capture: true, passive: true } as const;
    window.addEventListener('pointerdown', on, opts);
    window.addEventListener('keydown', on, opts);
    return () => { window.removeEventListener('pointerdown', on, opts); window.removeEventListener('keydown', on, opts); };
  }, []);

  // In-page schedule: re-armed on settings change and whenever the app comes back to the foreground.
  useEffect(() => {
    let timer = 0;
    const arm = () => {
      clearTimeout(timer);
      const n = nextAdhan();
      if (!n) return;
      const wait = Math.max(0, n.at.getTime() - Date.now());
      // Long waits are re-armed in steps (timers drift while the device sleeps).
      timer = window.setTimeout(() => {
        if (Math.abs(Date.now() - n.at.getTime()) < 60_000) void playAdhan(n.key, n.tag);
        arm();
      }, Math.min(wait, 10 * 60_000));
    };
    arm();
    const vis = () => {
      if (document.visibilityState === 'visible') { stopKeepAwake(); arm(); } else startKeepAwake();
    };
    document.addEventListener('visibilitychange', vis);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', vis); };
  }, [prayer, adhanPrefs]);

  // Second trigger: the service worker's notification timer / push while the app is open.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const on = (e: MessageEvent) => {
      if (e.data?.type !== 'PLAY_ADHAN') return;
      const tag = String(e.data.tag ?? '');
      const key = (tag.split('adhan-')[1] ?? 'dhuhr') as PrayerKey;
      void playAdhan(key, tag || undefined);
    };
    navigator.serviceWorker.addEventListener('message', on);
    return () => navigator.serviceWorker.removeEventListener('message', on);
  }, []);
}

// Dev/test hook (like window.__player): the live adhan state.
if (import.meta.env.DEV && typeof window !== 'undefined') (window as unknown as { __adhan: typeof useAdhan }).__adhan = useAdhan;
